import type { ReactElement } from 'react';
import type { QueueItemDto } from '@gr/core/distribution/scheduling/dto';
import { BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import type { OperationResult } from '@gr/utils/operation-result';
import { useServices, useT } from '../../../app/di';
import { moveItem } from '../../../shared/collections';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { EmptyState } from '../../../ui-kit/EmptyState/EmptyState';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { useBlog } from '../../accounts/workspaces';
import { QueueList } from './QueueList/QueueList';

type QueueChange = () => Promise<OperationResult<QueueItemDto[]>>;

// SCH-UC-3, SCH-UC-4, SCH-RULE-2
export function QueuePage(): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const { blog, can } = useBlog();
  const queue = useResource(() => api.scheduling.queue(blog.id), [api, blog.id]);
  const change = useAction((run: QueueChange) => run());

  const apply = async (run: QueueChange): Promise<void> => {
    const result = await change.run(run);
    if (result.success) queue.setData(result.data);
  };

  const items = queue.data ?? [];
  return (
    <div className="u-stack-lg">
      <PageHeader title={t('scheduling.queue.title')} subtitle={t('scheduling.queue.subtitle')} />
      {change.error ? <Alert tone="danger">{te(change.error)}</Alert> : null}
      {queue.loading && !queue.data ? (
        <Loading label={t('common.loading')} />
      ) : queue.error ? (
        <Alert tone="danger" title={t('common.loadFailed')}>
          <Button size="sm" onClick={() => void queue.reload()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : items.length === 0 ? (
        <EmptyState title={t('scheduling.queue.empty')} text={t('scheduling.queue.emptyText')} />
      ) : (
        <QueueList
          blogId={blog.id}
          timezone={blog.timezone}
          items={items}
          editable={can(BlogPermission.Plan)}
          pending={change.pending}
          onMove={(index, delta) => void apply(() => api.scheduling.reorder(blog.id, moveItem(items, index, delta).map((item) => item.id)))}
          onRemove={(item) => void apply(() => api.scheduling.dequeue(blog.id, item.id))}
        />
      )}
    </div>
  );
}
